import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';
import { S, fmtWhen } from './adminPageStyles';
import { CoveragePanel } from './CoveragePanel';

/** Data quality (SRS #115–#119): findings from the validation rules, per area/kind/source, with dismiss and re-run. */

const KIND_LABEL: Record<string, string> = {
  source_url_missing: 'No source URL', source_url_malformed: 'Malformed source URL', api_url_not_public: 'Source URL is an API endpoint', unofficial_source: 'Unofficial source host',
  title_missing: 'No title', title_is_filename: 'Title is a file name', jurisdiction_missing: 'No jurisdiction', content_too_short: 'Content too short to cite', summary_unusable: 'Summary unusable', possible_duplicate: 'Possible duplicate',
  status_missing: 'No status', status_unrecognized: 'Unrecognised status', action_date_in_future: 'Action dated in the future', enforced_but_far_future: 'In force but effective far in future', pending_but_effective_long_ago: 'Pending but effective long ago', inactive_but_future_effective: 'Inactive but future effective date',
};

const AdminDataQualityPage: React.FC = () => {
  const { sidebarCollapsed, setSidebarCollapsed, activeSection, setSelectedRegulation, handleNavClick, handleLogout } = useAdminNavigation();
  const token = window.localStorage.getItem('policyora_token') ?? '';
  const [summary, setSummary] = useState<adminApi.DQSummary | null>(null);
  const [items, setItems] = useState<adminApi.DQFinding[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<{ area?: string; kind?: string; source?: string; severity?: string }>({});
  const [offset, setOffset] = useState(0);
  const [vocab, setVocab] = useState<{ learned: Record<string, string>; unknown: { status: string; count: number }[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'findings' | 'coverage'>('findings');   // SRS #167
  const PAGE = 50;

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, f, v] = await Promise.all([
        adminApi.getDataQualitySummary(token),
        adminApi.getDataQualityFindings(token, { ...filter, limit: PAGE, offset }),
        adminApi.getStatusVocab(token),
      ]);
      setSummary(s); setItems(f.items); setTotal(f.total); setVocab({ learned: v.learned, unknown: v.unknown });
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }, [token, filter, offset]);
  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key); setError(null);
    try { await fn(); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); } finally { setBusy(null); }
  };

  const chip = (label: string, active: boolean, onClick: () => void, count?: number) => (
    <button key={label} type="button" onClick={onClick} style={{ padding: '4px 10px', borderRadius: 999, border: '1px solid var(--ds-border-strong)', background: active ? 'var(--ds-brand-blue)' : 'var(--ds-surface)', color: active ? 'var(--ds-text-on-dark)' : 'var(--ds-text-secondary)', cursor: 'pointer', fontSize: 12.5 }}>
      {label}{count !== undefined ? ` · ${count}` : ''}
    </button>
  );

  return (
    <DashboardLayout collapsed={sidebarCollapsed} activeSection={activeSection} welcomeLabel="Data Quality" selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((p) => !p)} onNavClick={handleNavClick} onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout} onRegulationModalClose={() => setSelectedRegulation(null)}>
      <section style={S.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={S.h2}>Data quality</h2>
            <p style={S.sub}>Every regulation is checked daily for source traceability, reference completeness and status consistency. Findings resolve themselves when the data is fixed; dismiss one to accept it.</p>
          </div>
          <button style={S.primary} disabled={busy === 'run'} onClick={() => act('run', () => adminApi.runDataQuality(token))}>{busy === 'run' ? 'Scanning…' : 'Run validation now'}</button>
        </div>
        {error && <div style={S.error}>{error}</div>}
        <div style={S.tabs} role="tablist">
          <button role="tab" aria-selected={tab === 'findings'} style={S.tab(tab === 'findings')} onClick={() => setTab('findings')}>Findings</button>
          <button role="tab" aria-selected={tab === 'coverage'} style={S.tab(tab === 'coverage')} onClick={() => setTab('coverage')}>Coverage acceptance</button>
        </div>
        {tab === 'coverage' && <CoveragePanel token={token} />}

        {tab === 'findings' && summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, margin: '12px 0 16px' }}>
            {[
              ['Open findings', summary.open.total],
              ['High severity', summary.open.by_severity.high || 0],
              ['Last scan', summary.last_run ? `${summary.last_run.scanned.toLocaleString()} records` : '—'],
              ['New last run', summary.last_run?.new_findings ?? '—'],
              ['Resolved last run', summary.last_run?.resolved_findings ?? '—'],
              ['Last run at', summary.last_run ? fmtWhen(summary.last_run.finished_at) : 'never'],
            ].map(([l, v]) => (
              <div key={String(l)} style={{ border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.6rem 0.9rem' }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ds-text-muted)' }}>{l}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--ds-text)' }}>{String(v)}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'findings' && (<>
        {summary && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            {chip('All areas', !filter.area, () => { setFilter({ ...filter, area: undefined, kind: undefined }); setOffset(0); })}
            {(['source', 'reference', 'status'] as const).map((a) => chip(a, filter.area === a, () => { setFilter({ ...filter, area: a, kind: undefined }); setOffset(0); }, summary.open.by_area[a] || 0))}
            <span style={{ width: 8 }} />
            {(['high', 'medium', 'low'] as const).map((sv) => chip(sv, filter.severity === sv, () => { setFilter({ ...filter, severity: filter.severity === sv ? undefined : sv }); setOffset(0); }, summary.open.by_severity[sv] || 0))}
            <select style={{ ...S.input, marginLeft: 'auto', minWidth: 200 }} value={filter.kind || ''} onChange={(e) => { setFilter({ ...filter, kind: e.target.value || undefined }); setOffset(0); }}>
              <option value="">All kinds</option>
              {Object.entries(summary.open.by_kind).sort((a, b) => b[1] - a[1]).map(([k, n]) => <option key={k} value={k}>{KIND_LABEL[k] || k} · {n}</option>)}
            </select>
            <select style={{ ...S.input, minWidth: 160 }} value={filter.source || ''} onChange={(e) => { setFilter({ ...filter, source: e.target.value || undefined }); setOffset(0); }}>
              <option value="">All sources</option>
              {Object.entries(summary.open.by_source).sort((a, b) => b[1] - a[1]).map(([k, n]) => <option key={k} value={k}>{k} · {n}</option>)}
            </select>
          </div>
        )}

        <table style={S.table}>
          <thead><tr><th style={S.th}>Severity</th><th style={S.th}>Finding</th><th style={S.th}>Regulation</th><th style={S.th}>Source</th><th style={S.th}>Detail</th><th style={S.th}>Since</th><th style={S.th} /></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td style={{ ...S.td, ...S.muted }} colSpan={7}>No open findings match.</td></tr>}
            {items.map((f) => (
              <tr key={f.id}>
                <td style={S.td}><span style={{ ...S.chip, background: f.severity === 'high' ? 'rgba(180,35,24,0.1)' : f.severity === 'medium' ? 'rgba(247,144,9,0.15)' : undefined, color: f.severity === 'high' ? 'var(--ds-danger)' : f.severity === 'medium' ? 'var(--ds-warning)' : undefined }}>{f.severity}</span></td>
                <td style={S.td}>{KIND_LABEL[f.kind] || f.kind}<div style={{ fontSize: '0.7rem', color: 'var(--ds-text-faint)' }}>{f.area}</div></td>
                <td style={{ ...S.td, maxWidth: 280 }}><Link to={`/regulations/${f.artifact_id}`} style={{ color: 'var(--ds-text)' }}>{f.title || f.artifact_id}</Link><div style={{ fontSize: '0.72rem', color: 'var(--ds-text-muted)' }}>{f.jurisdiction_name || ''}</div></td>
                <td style={{ ...S.td, ...S.mono }}>{f.source_system || ''}</td>
                <td style={{ ...S.td, maxWidth: 360, whiteSpace: 'normal', fontSize: '0.8rem' }}>{f.detail}</td>
                <td style={S.td}>{fmtWhen(f.first_seen_at)}</td>
                <td style={S.td}><button style={S.btn} disabled={busy === `d${f.id}`} onClick={() => act(`d${f.id}`, () => adminApi.dismissDataQualityFinding(token, f.id))}>Dismiss</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={S.pager}>
          <button style={S.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Prev</button>
          <span>{total === 0 ? 'No findings' : `${offset + 1}–${Math.min(offset + PAGE, total)} of ${total}`}</span>
          <button style={S.btn} disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next →</button>
        </div>

        {vocab && (
          <div style={{ marginTop: 20, border: '1px solid var(--ds-border)', borderRadius: 10, padding: '0.9rem 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--ds-text)' }}>Status vocabulary</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--ds-text-muted)' }}>{vocab.unknown.length} unrecognised status strings in the corpus. The model can map them to enforced / pending / inactive; the mapping is stored and shown here, and applied on the next run.</div>
              </div>
              <button style={S.btn} disabled={busy === 'classify' || vocab.unknown.length === 0} onClick={() => act('classify', () => adminApi.classifyStatuses(token))}>{busy === 'classify' ? 'Classifying…' : 'Classify with AI'}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: 12, marginTop: 10, fontSize: '0.82rem' }}>
              <div><div style={S.muted}>Unrecognised</div>{vocab.unknown.map((u) => <div key={u.status}><span style={S.mono}>{u.status}</span> <span style={S.muted}>· {u.count}</span></div>)}{vocab.unknown.length === 0 && <div style={S.muted}>none</div>}</div>
              <div><div style={S.muted}>Learned</div>{Object.entries(vocab.learned).map(([k, v]) => <div key={k}><span style={S.mono}>{k}</span> → {v}</div>)}{Object.keys(vocab.learned).length === 0 && <div style={S.muted}>none yet</div>}</div>
            </div>
          </div>
        )}
        </>)}
      </section>
    </DashboardLayout>
  );
};

export default AdminDataQualityPage;
